import { ContentType, LessonMode } from '../../Constants';
import docReaderManager from '../managers/DocReaderManager';
import lessonManager from '../managers/LessonManager';
import responseManager from '../managers/ResponseManager';
import responseService from './ResponseService';
import {
  getFirstLessonElementIdOnPdfPage,
} from './PdfActivityService';

export function getSavedPdfNavigation(responseAnswer) {
  if (!responseAnswer || responseAnswer.pdfLastPage == null) {
    return null;
  }
  return {
    pdfLastPage: Number(responseAnswer.pdfLastPage),
    pdfLastChildLessonElementId: responseAnswer.pdfLastChildLessonElementId ?? null,
    pdfManualChildNav: !!responseAnswer.pdfManualChildNav,
  };
}

class PdfActivityNavigationService {
  restoredParentIds = new Set();

  /** Last reader page processed by slide sync — used to detect user PDF page changes vs child-only nav. */
  lastReaderSyncPage = null;

  isPreviewLikeMode = () => {
    const mode = lessonManager.playerMode;
    return mode === LessonMode.PREVIEW ||
      mode === LessonMode.PUBLISHER_PREVIEW ||
      mode === LessonMode.ITEM_PREVIEW ||
      mode === LessonMode.STUDENT_PREVIEW;
  };

  canPersistPdfNavigation = () => {
    const mode = lessonManager.playerMode;
    if (mode === LessonMode.SCORING || mode === LessonMode.REVIEW) {
      return false;
    }
    if (lessonManager.isReadOnly) {
      return false;
    }
    return mode === LessonMode.ACTIVITY_MODE ||
      mode === LessonMode.TEACHER_PRESENTATION ||
      mode === LessonMode.PREVIEW ||
      mode === LessonMode.PUBLISHER_PREVIEW ||
      mode === LessonMode.ITEM_PREVIEW;
  };

  getParentResponseAnswer = (parentLessonElementId) => {
    const state = responseManager.getLessonElementState(parentLessonElementId);
    if (!state) {
      return null;
    }
    const { currentResponseAnswer, cachedResponseAnswer } = state;
    if (currentResponseAnswer?.pdfLastPage != null) {
      return currentResponseAnswer;
    }
    if (this.isPreviewLikeMode() && cachedResponseAnswer?.pdfLastPage != null) {
      return cachedResponseAnswer;
    }
    if (this.isPreviewLikeMode() && cachedResponseAnswer) {
      return cachedResponseAnswer;
    }
    return currentResponseAnswer;
  };

  getSavedNavigation = (parentLessonElementId) => {
    return getSavedPdfNavigation(this.getParentResponseAnswer(parentLessonElementId));
  };

  hasVisibleChildOnPage = (parentModel, pageNum, filteredSlides = null) => {
    const activityEl = lessonManager.getLessonElement(parentModel.lessonElementId);
    const orderedIds = activityEl?.lessonElementIds;
    if (!orderedIds?.length) {
      return false;
    }
    const slides = filteredSlides ?? lessonManager.getFilteredSlides();
    return !!getFirstLessonElementIdOnPdfPage(
      parentModel,
      pageNum,
      orderedIds,
      (id) => lessonManager.getSlideModel(id),
      slides
    );
  };

  /**
   * True when the PDF reader is on page 1 with no lesson child linked to that page.
   * Derived from live reader page — does not rely on a separate UI flag staying in sync.
   */
  shouldShowBlankPanel = (parentLessonElementId, page) => {
    if (docReaderManager.pdfManualChildNav) {
      return false;
    }
    const parentModel = lessonManager.getSlideModel(parentLessonElementId);
    if (!parentModel || parentModel.type !== ContentType.PDF_ACTIVITY.type) {
      return false;
    }
    const n = Number(page);
    if (page == null || Number.isNaN(n) || n !== 1) {
      return false;
    }
    return !this.hasVisibleChildOnPage(parentModel, 1);
  };

  pageHasNoLinkedQuestions = (parentModel, page) => {
    return this.shouldShowBlankPanel(parentModel.lessonElementId, page);
  };

  showBlankQuestionPanel = (parentLessonElementId) => {
    docReaderManager.setPdfQuestionPanelBlank(true);
    docReaderManager.setPdfDisplayChildLessonElementId(null);
    docReaderManager.setPdfManualChildNav(false);
  };

  showChildQuestionPanel = (parentLessonElementId, childLessonElementId = null) => {
    docReaderManager.setPdfQuestionPanelBlank(false);
    docReaderManager.setPdfDisplayChildLessonElementId(childLessonElementId);
  };

  writeNavigationFields = (parentLessonElementId, {
    page,
    childLessonElementId = null,
    manualChildNav = false,
  } = {}) => {
    const lessonElementState = responseManager.getLessonElementState(parentLessonElementId);
    if (!lessonElementState?.currentResponseAnswer) {
      return false;
    }
    const nextPage = page != null ? Number(page) : null;
    Object.assign(lessonElementState.currentResponseAnswer, {
      pdfLastPage: nextPage,
      pdfLastChildLessonElementId: childLessonElementId,
      pdfManualChildNav: !!manualChildNav,
    });
    return true;
  };

  /**
   * Page 1 with no linked children: blank question panel, no nav selection.
   * Runs before pending-nav guards so preview/activity entry cannot skip it.
   */
  tryApplyBlankPanelForPage = (parentLessonElementId, page, { persist = true } = {}) => {
    if (!this.shouldShowBlankPanel(parentLessonElementId, page)) {
      return false;
    }
    docReaderManager.clearPdfEntryViaActivityParentSlide();
    this.markReaderPageSynced(page);
    this.showBlankQuestionPanel(parentLessonElementId);
    if (persist && !this._isRestoring) {
      this.persistState(parentLessonElementId, {
        page,
        childLessonElementId: null,
        manualChildNav: false,
      }).catch((err) => console.error(err));
    }
    return true;
  };

  setManualChildNav = (parentLessonElementId, childLessonElementId) => {
    docReaderManager.setPdfManualChildNav(true);
    docReaderManager.setPdfQuestionPanelBlank(false);
    docReaderManager.setPdfDisplayChildLessonElementId(childLessonElementId);
    this.persistState(parentLessonElementId, {
      page: docReaderManager.currentPageNumber,
      childLessonElementId,
      manualChildNav: true,
    });
  };

  persistState = async (parentLessonElementId, {
    page,
    childLessonElementId = null,
    manualChildNav = false,
  } = {}) => {
    if (this._isRestoring) {
      return;
    }
    this.writeNavigationFields(parentLessonElementId, {
      page,
      childLessonElementId,
      manualChildNav,
    });
    if (!this.canPersistPdfNavigation()) {
      return;
    }

    if (this.isPreviewLikeMode()) {
      responseManager.setPreviewResponseSubmitted(parentLessonElementId, true);
      return;
    }

    const mode = lessonManager.playerMode;
    if (mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION) {
      await responseService.saveSingleResponse(parentLessonElementId);
    }
  };

  beginRestore = (parentLessonElementId) => {
    if (this.restoredParentIds.has(parentLessonElementId)) {
      return false;
    }
    this.restoredParentIds.add(parentLessonElementId);
    this._isRestoring = true;
    return true;
  };

  endRestore = () => {
    this._isRestoring = false;
  };

  applySavedNavigationUi = (parentLessonElementId, saved) => {
    const parentModel = lessonManager.getSlideModel(parentLessonElementId);
    if (!saved?.pdfLastPage) {
      const page = docReaderManager.currentPageNumber;
      this.lastReaderSyncPage = page;
      if (parentModel && this.tryApplyBlankPanelForPage(parentLessonElementId, page, { persist: false })) {
        return { needsChildNav: false, page: null };
      }
      return { needsChildNav: false, page: null };
    }

    docReaderManager.goToPage(saved.pdfLastPage);
    this.lastReaderSyncPage = saved.pdfLastPage;

    if (saved.pdfLastChildLessonElementId == null) {
      this.showBlankQuestionPanel(parentLessonElementId);
      return { needsChildNav: false, page: saved.pdfLastPage };
    }

    this.showChildQuestionPanel(parentLessonElementId, null);
    if (saved.pdfManualChildNav) {
      docReaderManager.setPdfManualChildNav(true);
      docReaderManager.setPdfDisplayChildLessonElementId(saved.pdfLastChildLessonElementId);
    }
    return {
      needsChildNav: true,
      childLessonElementId: saved.pdfLastChildLessonElementId,
      page: saved.pdfLastPage,
    };
  };

  /** After iframe load / page sync — preview often needs a deferred pass once pageCurrent is known. */
  refreshBlankPanelFromReader = (parentLessonElementId) => {
    const page = docReaderManager.currentPageNumber;
    if (page == null || page < 1) {
      return;
    }
    this.tryApplyBlankPanelForPage(parentLessonElementId, page);
  };

  clearSessionForParent = (parentLessonElementId) => {
    this.restoredParentIds.delete(parentLessonElementId);
    this.lastReaderSyncPage = null;
    docReaderManager.clearPdfQuestionPanelOverrides();
  };

  readerPageChangedSinceLastSync = (page) => {
    return this.lastReaderSyncPage == null || Number(page) !== Number(this.lastReaderSyncPage);
  };

  markReaderPageSynced = (page) => {
    this.lastReaderSyncPage = page != null ? Number(page) : null;
  };
}

export default new PdfActivityNavigationService();
